import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, img, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ActionDialog, Btn, PageHead } from "@/admin/ui";
import { ProductEditor } from "@/admin/ProductEditor";

export default function Products() {
  const { refresh } = useStore();
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const load = () => api.get("/admin/products").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const done = async () => { await load(); await refresh(); };

  const patch = async (p, changes) => {
    try { await api.put(`/admin/products/${p.id}`, { ...p, ...changes }); await done(); toast.success("Updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (i, d) => {
    const ids = list.map((p) => p.id);
    [ids[i], ids[i + d]] = [ids[i + d], ids[i]];
    await api.post("/admin/products/reorder", { ids });
    await done();
  };

  return (
    <div data-testid="admin-products">
      <PageHead title="Products" sub="Arrange order with the arrows — the first three appear in The Selected Three." action={<Btn variant="berry" onClick={() => setEdit({})} data-testid="add-product-button"><Plus className="w-4 h-4" />Add product</Btn>} />
      <div className="space-y-3">
        {(list || []).map((p, i) => (
          <div key={p.id} className="flex flex-wrap items-center gap-4 rounded-[22px] bg-white border border-plum/10 p-3 pr-5" data-testid={`admin-product-row-${p.slug}`}>
            <img src={img(p.images[0])} alt="" className="w-20 h-24 rounded-[14px] object-cover bg-pearl" />
            <div className="flex-1 min-w-[180px]">
              <p className="font-display text-[24px] font-semibold leading-tight">{p.name}</p>
              <p className="text-[14px] text-plum/65">{p.type} · {inr(p.effective_price)}{p.sale_price ? ` (was ${inr(p.price)})` : ""} · <b className={p.available <= 3 ? "text-ruby" : ""}>{p.available} in stock</b></p>
            </div>
            <label className="flex items-center gap-2 text-[13px] font-bold"><Switch checked={p.published} onCheckedChange={(v) => patch(p, { published: v })} data-testid={`publish-toggle-${p.slug}`} />{p.published ? "Live" : "Hidden"}</label>
            <button onClick={() => patch(p, { featured: !p.featured })} className={`h-10 px-3 rounded-[12px] flex items-center gap-1.5 text-[12px] font-extrabold uppercase ${p.featured ? "bg-champagne text-plum" : "bg-pearl text-plum/60"}`} data-testid={`feature-toggle-${p.slug}`}><Star className={`w-4 h-4 ${p.featured ? "fill-plum" : ""}`} />Featured</button>
            <div className="flex gap-1">
              <button disabled={i === 0} onClick={() => move(i, -1)} className="w-10 h-10 rounded-[12px] bg-pearl grid place-items-center disabled:opacity-30" aria-label="Move up" data-testid={`move-up-${p.slug}`}><ArrowUp className="w-4 h-4" /></button>
              <button disabled={i === list.length - 1} onClick={() => move(i, 1)} className="w-10 h-10 rounded-[12px] bg-pearl grid place-items-center disabled:opacity-30" aria-label="Move down" data-testid={`move-down-${p.slug}`}><ArrowDown className="w-4 h-4" /></button>
            </div>
            <Btn variant="light" onClick={() => setEdit(p)} data-testid={`edit-product-${p.slug}`}><Pencil className="w-4 h-4" />Edit</Btn>
            <button onClick={() => setDel(p)} className="w-10 h-10 rounded-[12px] text-ruby hover:bg-ruby/10 grid place-items-center" aria-label="Delete" data-testid={`delete-product-${p.slug}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {edit && <ProductEditor product={edit} onClose={() => setEdit(null)} onSaved={done} />}
      <ActionDialog open={!!del} onClose={() => setDel(null)} title="Delete this product?" description={del ? `${del.name} will be removed from your store. Existing orders keep their details.` : ""} confirmLabel="Delete" variant="red" testId="delete-product-dialog"
        onConfirm={async () => { await api.delete(`/admin/products/${del.id}`); await done(); toast.success("Product deleted"); }} />
    </div>
  );
}
